import { useCallback, useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";

const ARM_RESET_MS = 5000;

interface ConfirmButtonProps {
  onConfirm: () => void;
  /** Icône affichée sur le bouton (défaut : croix). */
  icon?: LucideIcon;
}

/**
 * Première pression : état « armé » (icône rouge).
 * Seconde pression : appelle onConfirm. Timeout et clic extérieur désarme.
 */
export function ConfirmButton({
  onConfirm,
  icon: Icon = X,
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), ARM_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [armed]);

  useEffect(() => {
    if (!armed) return;
    const onDocDown = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setArmed(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [armed]);

  useEffect(() => {
    if (!armed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setArmed(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [armed]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!armed) {
        setArmed(true);
        return;
      }
      onConfirm();
      setArmed(false);
    },
    [armed, onConfirm]
  );

  const idle =
    "text-gray-600 hover:bg-blue-600 hover:text-white dark:text-gray-400 dark:hover:bg-blue-600 dark:hover:text-white";
  const armedStyles =
    "text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40";

  return (
    <button
      ref={buttonRef}
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleClick}
      className={`rounded-full p-1.5 transition-colors ${
        armed ? armedStyles : idle
      }`}
      aria-pressed={armed}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
    </button>
  );
}
